import { useRef, useState } from "react";
import { MIN_REVIEW_RENDITION_SIDE, reviewImageSourceKey, reviewImageSources } from "./reviewImageSources";
import type { ReviewImage } from "./types";

type ImageState = "loading" | "loaded" | "error";
type ImageEntry = {
  state: ImageState;
  attempt: number;
  sourceIndex: number;
  width?: number;
  height?: number;
  ref?: (node: HTMLImageElement | null) => void;
  onLoad?: (event: { currentTarget: HTMLImageElement }) => void;
  onError?: () => void;
};

/** Browser evidence belongs to this exact Amazon source chain and request attempt. */
export function useReviewImageLoading(scope: string, revoke: (key: string) => void) {
  const [, render] = useState(0);
  const current = useRef<{ scope: string; entries: Map<string, ImageEntry> }>({ scope, entries: new Map() });
  const revokeRef = useRef(revoke);
  revokeRef.current = revoke;
  if (current.current.scope !== scope) current.current = { scope, entries: new Map() };
  const idFor = (image: ReviewImage & { key: string }) => reviewImageSources(image).length ? reviewImageSourceKey(image) : `missing:${image.key}`;
  const entryFor = (image: ReviewImage & { key: string }) => {
    const id = idFor(image);
    let entry = current.current.entries.get(id);
    if (!entry) { entry = { state: "loading", attempt: 0, sourceIndex: 0 }; current.current.entries.set(id, entry); }
    return entry;
  };
  const ready = (image: ReviewImage & { key: string }) => Boolean(reviewImageSources(image).length && current.current.scope === scope && entryFor(image).state === "loaded");
  const preview = (image: ReviewImage & { key: string }, alt: string, label: string, reload: () => void, disabled = false) => {
    const sources = reviewImageSources(image), id = idFor(image), entry = entryFor(image);
    const source = sources[entry.sourceIndex];
    const valid = () => current.current.scope === scope && current.current.entries.get(id) === entry;
    const replaceAttempt = (sourceIndex: number) => {
      if (!valid()) return;
      current.current.entries.set(id, { state: "loading", attempt: entry.attempt + 1, sourceIndex });
      revokeRef.current(image.key);
      render(value => value + 1);
    };
    const fail = () => {
      if (!valid() || entry.state === "error") return;
      if (entry.sourceIndex + 1 < sources.length) { replaceAttempt(entry.sourceIndex + 1); return; }
      entry.state = "error";
      revokeRef.current(image.key);
      render(value => value + 1);
    };
    const loaded = (node: HTMLImageElement) => {
      if (!valid() || entry.state === "error") return;
      const { naturalWidth: width, naturalHeight: height } = node;
      if (!(width > 0) || entry.sourceIndex > 0 && (!(height > 0) || Math.max(width, height) < MIN_REVIEW_RENDITION_SIDE)) { fail(); return; }
      if (entry.state === "loaded" && entry.width === width && entry.height === height) return;
      entry.state = "loaded"; entry.width = width; entry.height = height;
      render(value => value + 1);
    };
    // Cached successes and failures use the same guarded attempt as browser events.
    entry.ref ??= node => { if (node?.complete) loaded(node); };
    entry.onLoad ??= event => loaded(event.currentTarget);
    entry.onError ??= fail;
    const retry = () => { if (!disabled) replaceAttempt(0); };
    const reloadData = () => { if (!valid() || disabled) return; replaceAttempt(0); return reload(); };
    const state = source ? entry.state : "missing";
    const width = entry.width ?? source?.width, height = entry.height ?? source?.height;
    return <div className={`review-image-preview is-${state}`} data-image-state={state}>
      {source ? <a href={source.url} target="_blank" rel="noreferrer" aria-label={label}>
        <img key={`${scope}:${id}:${entry.attempt}`} data-review-image-key={image.key} src={source.url} alt={alt} loading="lazy" ref={entry.ref} onLoad={entry.onLoad} onError={entry.onError}/>
      </a> : <div className="review-image-placeholder">Amazon hat keine Bildadresse geliefert.</div>}
      <div className="review-image-load-status" role={state === "error" ? "alert" : "status"}>
        <span>{state === "loaded" ? entry.sourceIndex > 0 ? "Alternative Amazon-Auflösung geladen" : "Bild geladen" : state === "loading" ? "Bild wird geladen …" : state === "error" ? "Bild konnte nicht geladen werden." : "Sichtprüfung ohne Bild nicht möglich."}{state === "loaded" && width && height ? ` · ${width} × ${height} Pixel` : ""}</span>
        {state === "error" && <button type="button" className="button button--secondary" disabled={disabled} onClick={retry}>Bild erneut laden</button>}
        {(state === "error" || state === "missing") && <button type="button" className="button button--secondary" disabled={disabled} onClick={reloadData}>Bilddaten erneut laden</button>}
      </div>
    </div>;
  };
  return { ready, preview };
}
